<template>
  <Dialog
    :header="`Load ${sheetType} Template Set`"
    v-model:visible="visibleModel"
    :draggable="false"
    :style="{ width: '60vw' }"
    @hide="onHide"
  >
    <div class="row">
      <div class="col-md-12 p-4">
        <table class="p-datatable p-component p-datatable-hoverable-rows align-middle mb-0 w-12">
          <thead class="p-datatable-thead">
            <tr>
              <th style="width: 7%;">#</th>
              <th style="width: 20%;">Created Date</th>
              <th style="width: 20%;">Last Modified Date</th>
              <th style="width: 10%;">Version</th>
              <th style="width: 8%;">Sheets</th>
              <th style="width: 10%;" class="text-center">Status</th>
              <th style="width: 10%;" class="text-center">Action</th>
            </tr>
          </thead>
          <tbody class="p-datatable-tbody" v-if="!isLoading">
            <tr v-for="(row, index) in versions" :key="rowKey(row)"
              style="cursor: pointer" @click="onRowClick(row)">
              <td>{{ index + 1 }}</td>
              <td>{{ row.createdDate }}</td>
              <td>{{ row.lastModifiedDate }}</td>
              <td>{{ displayDraftVersion(row) }}</td>
              <td>{{ row.sheetCount }}</td>
              <td class="text-center">
                <Badge
                  class="mr-2"
                  :class="statusBadgeClass(row.status)"
                  :value="row.status"
                />
              </td>
              <td class="text-center" @click.stop>
                <div class="d-flex justify-content-center">
                  <Button
                    icon="pi pi-trash"
                    :title="isLoadedRow(row) ? 'Open in the editor - close it before deleting' : 'Delete'"
                    severity="danger" text rounded
                    :disabled="isLoadedRow(row)"
                    @click="onDelete(row)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="error-no-record" v-if="!isLoading && !versions.length">
      <FontAwesomeIcon :icon="['fas', 'image']" size="2x" />
      <h6><span>No Record Found</span></h6>
    </div>

    <TheTableLoading v-if="isLoading" />

    <template #footer>
      <div class="d-flex justify-content-end p-2">
        <Button class="p-button-text p-button-sm" label="Cancel" icon="pi pi-times" @click="onCancel" />
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Badge from 'primevue/badge';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import templateApi from '@/api/template-api';
import apiErrorMixin from '@/mixin/api-error';
import TheTableLoading from '@/components/TheTableLoading.vue';

interface VersionRow {
  version: number;
  createdDate: string | null;
  lastModifiedDate: string | null;
  sheetCount: number;
  isClosed: boolean;
  cloneVersion: number | null;
  importVersion: string | null;
  status: 'Draft';
}

export default defineComponent({
  mixins: [apiErrorMixin],
  name: 'TemplateSetPicker',
  components: { Dialog, Button, Badge, FontAwesomeIcon, TheTableLoading },
  props: {
    sheetType: { type: String as PropType<'RPK' | 'CWK'>, required: true },
    visible: { type: Boolean, default: false },
    // Currently loaded template to prevent deletion: Saving after deletion recreates every sheet, which collides with the unique sheet code
    loadedType: { type: String as PropType<'RPK' | 'CWK' | null>, default: null },
    loadedVersion: { type: Number as PropType<number | null>, default: null },
  },
  emits: ['update:visible', 'load'],
  data() {
    return {
      versions: [] as VersionRow[],
      isLoading: false,
    };
  },
  computed: {
    visibleModel: {
      get(): boolean { return this.visible; },
      set(v: boolean) { this.$emit('update:visible', v); },
    },
  },
  watch: {
    visible(next: boolean) {
      if (next) this.fetchVersions();
    },
  },
  methods: {
    rowKey(row: VersionRow): string {
      return `${row.version}`;
    },
    displayDraftVersion(row: VersionRow): string {
      // No publishedDate guard needed here - the listing is drafts only
      if (row.importVersion != null) return `${row.version}:${row.importVersion}`;
      if (row.cloneVersion != null) return `${row.version}:${row.cloneVersion}`;
      return `${row.version}`; // A set built from scratch in the editor was cloned from nothing and shows the draft number alone
    },
    statusBadgeClass(status: string): string {
      if (status === 'Active') return 'p-badge-success';
      if (status === 'Draft') return 'p-badge-danger';
      return 'p-badge-secondary';
    },
    async fetchVersions() {
      this.isLoading = true;
      this.versions = [];
      try {
        const res = await templateApi.templateSetVersions(this.sheetType);
        this.versions = res.data?.versions || [];
      } catch (e) {
        this.toastApiError(e, 'Failed to load template versions');
      } finally {
        this.isLoading = false;
      }
    },
    isLoadedRow(row: VersionRow): boolean {
      return this.loadedType === this.sheetType && this.loadedVersion === row.version;
    },
    onDelete(row: VersionRow) {
      this.$confirm.require({
        header: 'Confirmation',
        message: `Are you sure you want to delete all ${row.sheetCount} draft template(s) of ${this.sheetType} version ${this.displayDraftVersion(row)}?`,
        icon: 'pi pi-exclamation-triangle',
        accept: async () => {
          this.isLoading = true;
          try {
            const res = await templateApi.templateSetDelete(this.sheetType, row.version);
            this.$toast.add({
              severity: 'success', summary: 'Success',
              detail: res.data?.message || 'Templates deleted successfully.', life: 6000,
            });
          } catch (e) {
            this.toastApiError(e, 'Failed to delete templates');
          } finally {
            // Refresh either way: on failure the row may already be gone for another reason.
            await this.fetchVersions();
          }
        },
      });
    },
    onCancel() { this.visibleModel = false; },
    onHide() { this.visibleModel = false; },
    onRowClick(row: any) {
      this.$emit('load', {
        type: this.sheetType,
        version: row.version,
      });
      this.visibleModel = false;
    },
  },
});
</script>
